/**
 * 환경에 따라 asset 경로를 동적으로 생성하는 유틸리티 함수
 * GitHub Pages: /home prefix 필요
 * S3/CloudFront: prefix 없음
 */

/**
 * 현재 배포 환경을 감지합니다.
 * @returns 'github-pages' | 's3-cloudfront' | 'development'
 */
export const getDeploymentType = (): 'github-pages' | 's3-cloudfront' | 'development' => {
  // 빌드 시 환경변수로 명시적 설정
  if (process.env.REACT_APP_DEPLOYMENT_TYPE === 's3-cloudfront') {
    return 's3-cloudfront';
  }
  
  // 개발 환경
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }
  
  // 프로덕션에서 도메인 기반 감지
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // GitHub Pages 도메인 감지
    if (hostname.includes('github.io')) {
      return 'github-pages';
    }
    
    // 커스텀 도메인 (S3/CloudFront)
    if (hostname.includes('koreaorder.com') || hostname !== 'localhost') {
      return 's3-cloudfront';
    }
  }
  
  // 기본값: GitHub Pages (package.json의 homepage 설정 기반)
  return 'github-pages';
};

/**
 * 환경에 따라 올바른 asset 경로를 반환합니다.
 * @param assetPath - public 폴더 기준 상대 경로 (예: "/assets/lotties/Login.lottie")
 * @returns 환경에 맞는 절대 경로
 */
export const getAssetPath = (assetPath: string): string => {
  // assetPath가 이미 완전한 URL인 경우 그대로 반환
  if (assetPath.startsWith('http') || assetPath.startsWith('//')) {
    return assetPath;
  }
  
  // 슬래시로 시작하지 않으면 추가
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  
  const deploymentType = getDeploymentType();
  
  switch (deploymentType) {
    case 'github-pages':
    case 'development':
      return `/home${normalizedPath}`;
    
    case 's3-cloudfront':
    default:
      return normalizedPath;
  }
};

/**
 * Lottie 파일의 경로를 반환하는 편의 함수
 * @param filename - lottie 파일명 (예: "Login.lottie")
 * @returns 환경에 맞는 lottie 파일 경로
 */
export const getLottiePath = (filename: string): string => {
  return getAssetPath(`/assets/lotties/${filename}`);
};

/**
 * 이미지 파일의 경로를 반환하는 편의 함수
 * @param imagePath - 이미지 경로 (예: "/images/logo.png")
 * @returns 환경에 맞는 이미지 파일 경로
 */
export const getImagePath = (imagePath: string): string => {
  return getAssetPath(imagePath);
};

/**
 * 메뉴 기본 이미지(logo_mono.png)의 경로를 반환하는 함수
 * @returns 환경에 맞는 logo_mono.png 파일 경로
 */
export const getLogoMonoPath = (): string => {
  return getAssetPath('/images/logos/logo_mono.png');
}; 